import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface FundNameOption {
  id: string;
  name: string;
  source: 'user' | 'ai';
  isEditing: boolean;
}

export interface FundNameDialogData {
  options: FundNameOption[];
  selectedFundName: string;
  isLoading: boolean;
}

export interface FundNameDialogResult {
  selectedFundName: string;
  options: FundNameOption[];
}

@Component({
  selector: 'wai-fund-name-dialog',
  templateUrl: './fund-name-dialog.component.html',
  styleUrls: ['./fund-name-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FundNameDialogComponent {
  public fundNameOptions: FundNameOption[];
  public selectedFundName: string;
  public isFundNameSuggestionsLoading: boolean;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly dialogRef: MatDialogRef<FundNameDialogComponent, FundNameDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: FundNameDialogData,
  ) {
    this.fundNameOptions = data.options.map((option) => ({ ...option }));
    this.selectedFundName = data.selectedFundName;
    this.isFundNameSuggestionsLoading = data.isLoading;
  }

  public updateState(data: Partial<FundNameDialogData>): void {
    if (data.options) {
      this.fundNameOptions = data.options.map((option) => ({ ...option }));
    }

    if (typeof data.selectedFundName === 'string') {
      this.selectedFundName = data.selectedFundName;
    }

    if (typeof data.isLoading === 'boolean') {
      this.isFundNameSuggestionsLoading = data.isLoading;
    }

    this.cdr.markForCheck();
  }

  public getFundNameSourceTooltip(option: FundNameOption): string {
    return option.source === 'ai' ? 'AI-generated' : 'User-provided';
  }

  public selectFundName(option: FundNameOption): void {
    this.selectedFundName = option.name;
  }

  public enableFundNameEdit(option: FundNameOption, event?: Event): void {
    event?.stopPropagation();
    this.fundNameOptions.forEach((item) => {
      item.isEditing = item.id === option.id;
    });
    this.selectedFundName = option.name;
  }

  public saveFundNameEdit(option: FundNameOption): void {
    option.name = (option.name || '').trim();
    option.source = 'user';
    option.isEditing = false;
    this.selectedFundName = option.name;
  }

  public handleFundNameAction(option: FundNameOption, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (option.isEditing) {
      this.saveFundNameEdit(option);
      return;
    }

    this.enableFundNameEdit(option, event);
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public confirm(): void {
    const selectedFundName = (this.selectedFundName || '').trim();
    if (!selectedFundName) {
      return;
    }

    this.dialogRef.close({
      selectedFundName,
      options: this.fundNameOptions.map((option) => ({
        ...option,
        name: (option.name || '').trim(),
        isEditing: false,
      })),
    });
  }
}
